export interface MatrixAccountContext {
    principalTenant: string;
    principalUserId: string;
    tenant: string;
    userId: string;
    role: string;
    isImpersonating: boolean;
    isSuperadmin: boolean;
}

export interface PersistedPrincipal {
    principalTenant: string | null;
    principalUserId: string | null;
}

export const buildMatrixAccountContextKey = (context: MatrixAccountContext): string => [
    context.principalTenant,
    context.principalUserId,
    context.tenant,
    context.userId,
    context.role,
    context.isImpersonating ? 'impersonating' : 'direct',
    context.isSuperadmin ? 'superadmin' : 'standard',
].join(':');

export const shouldResetMatrixForAccountChange = ({
    activeContextKey,
    cleanupFailed = false,
    persistedPrincipal,
    nextContext,
}: {
    activeContextKey: string | null;
    cleanupFailed?: boolean;
    persistedPrincipal: PersistedPrincipal;
    nextContext: MatrixAccountContext;
}): boolean => {
    if (cleanupFailed) {
        return true;
    }

    const nextContextKey = buildMatrixAccountContextKey(nextContext);
    if (activeContextKey && activeContextKey !== nextContextKey) {
        return true;
    }

    const hasPersistedPrincipal = Boolean(
        persistedPrincipal.principalTenant && persistedPrincipal.principalUserId
    );

    return hasPersistedPrincipal && (
        persistedPrincipal.principalTenant !== nextContext.principalTenant ||
        persistedPrincipal.principalUserId !== nextContext.principalUserId
    );
};

export const enforceMatrixAccountBoundary = async ({
    activeContextKey,
    cleanupFailed = false,
    persistedPrincipal,
    nextContext,
    resetMatrixSession,
}: {
    activeContextKey: string | null;
    cleanupFailed?: boolean;
    persistedPrincipal: PersistedPrincipal;
    nextContext: MatrixAccountContext;
    resetMatrixSession: () => Promise<void>;
}): Promise<boolean> => {
    if (!shouldResetMatrixForAccountChange({
        activeContextKey,
        cleanupFailed,
        persistedPrincipal,
        nextContext,
    })) {
        return false;
    }

    await resetMatrixSession();
    return true;
};
